import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Landmark, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp,
  Loader2, Plus, ShieldCheck, ShieldAlert, History as HistoryIcon,
} from 'lucide-react';
import { payoutAPI, accountingAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

// ── Constants ────────────────────────────────────────────────────
const METHOD_TYPES = ['Direct Deposit', 'Check'];
const ACCOUNT_TYPES = ['Checking', 'Savings'];
const STATUS_TONE = {
  'Approved':       'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Pending Review': 'bg-amber-100 text-amber-800 border-amber-200',
  'Rejected':       'bg-rose-100 text-rose-800 border-rose-200',
};
const STATUS_ICON = {
  'Approved':       CheckCircle2,
  'Pending Review': Clock,
  'Rejected':       XCircle,
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * PayeeEftCard — banking / EFT details for a single payee (Agent, Agency,
 * or Manager), mounted inside the corresponding profile "Payment" accordion
 * so KR admins (and eligible viewers) can view + submit + approve/reject
 * banking info without jumping to the standalone `/accounting/eft` page.
 *
 * Props:
 *   - payeeType    ('Agent'|'Agency'|'Manager', required)
 *                                    — server-side enum used by /payout/eft.
 *   - payeeId      (string, required) — NPN for agents, agency_id for
 *                                       agencies, manager_id for managers.
 *   - recipientType ('agent'|'agency'|'subagency'|'manager', required)
 *                                    — lowercase enum used by /accounting
 *                                       payment-info for the DD sync.
 *   - recipientId  (string, required) — id used by the sibling
 *                                       PaymentInformationCard for DD sync.
 *   - canManage    (bool)             — allow submit/approve/reject.
 *   - canView      (bool)             — allow reading (defaults true).
 *   - onDirectDepositSync (fn)        — called after an approval so the
 *                                       sibling PaymentInformationCard
 *                                       can refresh its status flag.
 *   - title        (string)           — override card title (default
 *                                       "Banking / EFT Details").
 *   - emptyLabel   (string)           — override empty state noun
 *                                       (default "banking info").
 */
export default function PayeeEftCard({
  payeeType, payeeId, recipientType, recipientId,
  canManage = false, canView = true, onDirectDepositSync,
  title = 'Banking / EFT Details',
  emptyLabel = 'banking info',
}) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [reviewing, setReviewing] = useState(null); // {eft_id, action}
  const [reviewNotes, setReviewNotes] = useState('');

  const load = useCallback(async () => {
    if (!payeeType || !payeeId) return;
    setLoading(true);
    try {
      const res = await payoutAPI.eft.history(payeeType, payeeId);
      // Route sorts version desc → newest first.
      setHistory(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, `Failed to load ${emptyLabel}`));
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [payeeType, payeeId, emptyLabel]);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(
    () => history.find((r) => r.is_current && r.status === 'Approved') || null,
    [history],
  );
  const pending = useMemo(
    () => history.find((r) => r.status === 'Pending Review') || null,
    [history],
  );
  const older = useMemo(
    () => history.filter((r) => r !== current && r !== pending),
    [history, current, pending],
  );

  // ── Review (approve/reject) ────────────────────────────────
  const doReview = async () => {
    if (!reviewing) return;
    try {
      await payoutAPI.eft.review(reviewing.eft_id, reviewing.action, reviewNotes || undefined);
      // On approval, auto-sync the Payment Information card's Direct
      // Deposit flag to "complete" so admins don't have to touch two
      // surfaces. Best-effort — surface the sync error but keep the
      // approval since the underlying EFT record is already updated.
      if (reviewing.action === 'approve' && recipientId && recipientType) {
        try {
          await accountingAPI.updateDirectDeposit(recipientType, recipientId, {
            status: 'complete',
            notes: `EFT ${reviewing.eft_id} approved`,
            external_reference: reviewing.eft_id,
          });
          onDirectDepositSync?.();
        } catch (sync_err) {
          console.warn('DD status sync failed', sync_err);
          toast.warning('EFT approved but Direct Deposit status sync failed — set it manually above.');
        }
      }
      toast.success(reviewing.action === 'approve' ? 'EFT approved' : 'EFT rejected');
      setReviewing(null);
      setReviewNotes('');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Review failed'));
    }
  };

  if (!canView) return null;

  return (
    <Card data-testid="payee-eft-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-heading font-semibold flex items-center gap-2">
            <Landmark size={16} className="text-[#0f2a5e]" />
            {title}
          </CardTitle>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSubmit(true)}
              data-testid="payee-eft-submit-btn"
            >
              <Plus size={12} className="mr-1" /> Add / Update banking info
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="py-6 flex items-center justify-center text-sm text-slate-500">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Pending Review — surfaced first so admins see it right away */}
            {pending && (
              <EftRow
                r={pending}
                highlight
                data-testid="payee-eft-pending"
                actions={canManage ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7"
                      onClick={() => { setReviewing({ eft_id: pending.eft_id, action: 'approve' }); setReviewNotes(''); }}
                      data-testid="payee-eft-approve-btn"
                    >
                      <ShieldCheck size={12} className="mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50 h-7"
                      onClick={() => { setReviewing({ eft_id: pending.eft_id, action: 'reject' }); setReviewNotes(''); }}
                      data-testid="payee-eft-reject-btn"
                    >
                      <ShieldAlert size={12} className="mr-1" /> Reject
                    </Button>
                  </div>
                ) : null}
              />
            )}

            {/* Current (approved) record */}
            {current ? (
              <EftRow r={current} data-testid="payee-eft-current" />
            ) : !pending ? (
              <div
                className="rounded border border-dashed border-slate-300 py-6 text-center text-sm text-slate-500"
                data-testid="payee-eft-empty"
              >
                No {emptyLabel} on file yet.
                {canManage && (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
                      onClick={() => setShowSubmit(true)}
                    >
                      <Plus size={12} className="mr-1" /> Add {emptyLabel}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}

            {/* History toggle */}
            {older.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                  data-testid="payee-eft-history-toggle"
                >
                  <HistoryIcon size={12} />
                  {expanded ? 'Hide history' : `Show history (${older.length} version${older.length === 1 ? '' : 's'})`}
                  {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {expanded && (
                  <div className="mt-2 space-y-2">
                    {older.map((r) => (
                      <EftRow key={r.eft_id} r={r} muted />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Submit / Update dialog */}
      <SubmitEftDialog
        open={showSubmit}
        onOpenChange={setShowSubmit}
        payeeType={payeeType}
        payeeId={payeeId}
        prefill={current}
        onSubmitted={() => { setShowSubmit(false); load(); }}
      />

      {/* Approve/Reject confirmation dialog */}
      <Dialog open={!!reviewing} onOpenChange={(v) => { if (!v) { setReviewing(null); setReviewNotes(''); } }}>
        <DialogContent data-testid="payee-eft-review-dialog">
          <DialogHeader>
            <DialogTitle>
              {reviewing?.action === 'approve' ? 'Approve banking info' : 'Reject banking info'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {reviewing?.action === 'approve'
                ? 'Approving will make this the current banking record on file and mark the agent\'s Direct Deposit status as complete.'
                : 'Rejecting will keep any previously approved record active. The agent can resubmit a new version.'}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={3}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={reviewing?.action === 'approve' ? 'e.g. Verified with agent by phone.' : 'e.g. Routing number does not match a valid bank.'}
              data-testid="payee-eft-review-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button
              onClick={doReview}
              className={reviewing?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
              data-testid="payee-eft-review-confirm"
            >
              {reviewing?.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Row renderer ────────────────────────────────────────────────
function EftRow({ r, highlight = false, muted = false, actions = null, ...rest }) {
  const Icon = STATUS_ICON[r.status] || Clock;
  return (
    <div
      className={`rounded border p-3 ${highlight
        ? 'border-amber-300 bg-amber-50'
        : muted
          ? 'border-slate-200 bg-slate-50/60'
          : 'border-slate-200 bg-white'}`}
      {...rest}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-[10.5px] border ${STATUS_TONE[r.status] || 'bg-slate-100'}`}>
              <Icon size={10} className="mr-1" /> {r.status}
            </Badge>
            <span className="text-[10.5px] text-slate-500 uppercase tracking-wider">v{r.version}</span>
            <span className="text-sm font-medium text-slate-900">{r.payment_method_type}</span>
            {r.is_current && (
              <Badge className="text-[10.5px] bg-[#0f2a5e] text-white">CURRENT</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Account holder</div>
              <div className="text-slate-900">{r.account_holder_name || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Bank</div>
              <div className="text-slate-900">{r.bank_name || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Type</div>
              <div className="text-slate-900">{r.account_type || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Routing / Account</div>
              <div className="text-slate-900 font-mono">
                {r.routing_number || '—'} · {r.account_number || '—'}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-100 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>Submitted {fmtDate(r.submitted_at)} by {r.submitted_by || '—'}</span>
            {r.reviewed_at && (
              <span>· Reviewed {fmtDate(r.reviewed_at)} by {r.reviewed_by || '—'}</span>
            )}
            {r.review_notes && <span>· &ldquo;{r.review_notes}&rdquo;</span>}
          </div>
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

// ── Submit dialog ───────────────────────────────────────────────
function SubmitEftDialog({ open, onOpenChange, payeeType, payeeId, prefill, onSubmitted }) {
  const [form, setForm] = useState({
    payment_method_type: 'Direct Deposit',
    account_holder_name: '',
    bank_name: '',
    account_type: 'Checking',
    routing_number: '',
    account_number: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Prefill from the current record so admins can start from what's on
  // file and change only the fields that need updating. Sensitive fields
  // (routing/account) come back masked so we deliberately do NOT prefill
  // them — the admin has to retype the full numbers.
  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setForm({
        payment_method_type: prefill.payment_method_type || 'Direct Deposit',
        account_holder_name: prefill.account_holder_name || '',
        bank_name: prefill.bank_name || '',
        account_type: prefill.account_type || 'Checking',
        routing_number: '',
        account_number: '',
        notes: '',
      });
    } else {
      setForm({
        payment_method_type: 'Direct Deposit',
        account_holder_name: '',
        bank_name: '',
        account_type: 'Checking',
        routing_number: '',
        account_number: '',
        notes: '',
      });
    }
  }, [open, prefill]);

  const submit = async () => {
    setSaving(true);
    try {
      await payoutAPI.eft.submit({
        payee_type: payeeType,
        payee_id: payeeId,
        ...form,
      });
      toast.success('Banking info submitted for review');
      onSubmitted?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Submission failed'));
    } finally {
      setSaving(false);
    }
  };

  const needsBank = form.payment_method_type === 'Direct Deposit';
  const canSubmit = form.account_holder_name.trim()
    && (!needsBank || (form.bank_name.trim() && form.routing_number.trim() && form.account_number.trim()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payee-eft-submit-dialog">
        <DialogHeader>
          <DialogTitle>{prefill ? 'Update banking info' : 'Add banking info'}</DialogTitle>
          <DialogDescription className="text-xs">
            {prefill
              ? 'A new version will be submitted for KR review. The current approved record stays active until the new one is approved.'
              : 'The submission goes to KR for review before it becomes the active payment method on file.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Payment method *</Label>
            <Select
              value={form.payment_method_type}
              onValueChange={(v) => setForm((f) => ({ ...f, payment_method_type: v }))}
            >
              <SelectTrigger data-testid="payee-eft-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHOD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Account holder name *</Label>
            <Input
              value={form.account_holder_name}
              onChange={(e) => setForm((f) => ({ ...f, account_holder_name: e.target.value }))}
              data-testid="payee-eft-holder"
            />
          </div>
          {needsBank && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Bank name *</Label>
                  <Input
                    value={form.bank_name}
                    onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                    data-testid="payee-eft-bank"
                  />
                </div>
                <div>
                  <Label className="text-xs">Account type</Label>
                  <Select
                    value={form.account_type}
                    onValueChange={(v) => setForm((f) => ({ ...f, account_type: v }))}
                  >
                    <SelectTrigger data-testid="payee-eft-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Routing number *</Label>
                  <Input
                    inputMode="numeric"
                    value={form.routing_number}
                    onChange={(e) => setForm((f) => ({ ...f, routing_number: e.target.value.replace(/\D/g, '') }))}
                    data-testid="payee-eft-routing"
                  />
                </div>
                <div>
                  <Label className="text-xs">Account number *</Label>
                  <Input
                    inputMode="numeric"
                    value={form.account_number}
                    onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value.replace(/\D/g, '') }))}
                    data-testid="payee-eft-account"
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Anything KR reviewers should know."
              data-testid="payee-eft-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !canSubmit}
            className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
            data-testid="payee-eft-submit-confirm"
          >
            {saving ? 'Submitting…' : 'Submit for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
